import { ALLink } from "../components/ALComponents";

const { clsx } = require("clsx");
const { twMerge } = require("tailwind-merge");

const LINK_COLOR_PATTERN = /\[([^\]]+)\]\{([^}]+)\}/g;

export function edgesToArray(root) {
  return root.edges.map((e) => e.node);
}

export function roundNumber(num) {
  if (Number.isNaN(parseFloat(num))) {
    return "";
  }

  const n = num % 1 === 0 ? 0 : 2;
  return parseFloat(Math.round(num * 10 ** n) / 10 ** n).toFixed(n);
}

export function validateEmail(email) {
  email = email.trim().toLowerCase();
  if (/^\S+@\S+\.\S+$/.test(email)) {
    return email;
  }
  return false;
}

/**
 * Poor man's markdown template engine. It doesn't process all possible cases but it works for the most common use cases such as :
 *
 * _{DISCOUNT} OFF_ Code: {CODE}  ==> [<span className="underline">10% OFF</span>, "Code: MA10"]
 * Code: *{CODE}* ==> ["Code: ", <span className="strong">MA10</span>]
 * Code: *_{CODE}_* ==> ["Code: ", <span className="strong"><span className="underline">MA10</span></span>]
 * [Join Club AL](/account/register) to *earn 2x gold pts* on purchases. ==>
 *          [<Link to="/account/register">Join Club AL</Link>, "to", ...]
 * mobile-only \n linebreak ==> ["mobile-only", <br className="mobile-only-linebreak"/>, "linebreak"]
 *
 * */
export function markdownFormat(sentence, varMap = []) {
  // resolve vars
  const regex = /\{([A-Z_]+)\}/g; // the g at the end prevents infinite-loop below
  let m;
  let resolvedSentence = "";
  let lastEnd = 0;
  do {
    m = regex.exec(sentence);
    if (m) {
      if (varMap.length() > 0) {
        const prefix = sentence?.slice(lastEnd, m.index);
        resolvedSentence += prefix + (varMap[m[1]] ?? m[1]);
      }
      lastEnd = m.index + m[0].length;
    }
  } while (m);
  const tail = sentence?.slice(lastEnd);
  resolvedSentence += tail;

  // process styles (underline and bold)
  const stack = [];
  const formatted = [];
  let s = "";
  for (let i = 0; i < resolvedSentence.length; i++) {
    const c = resolvedSentence[i];
    switch (c) {
      case "*":
      case "_":
        if (stack.length > 0 && stack[stack.length - 1] === c) {
          stack.pop();
          if (s.length > 0) {
            formatted.push(s);
            s = "";
          }
          formatted[formatted.length - 1] = (
            <span
              style={c === "*" ? { fontWeight: "bold" } : { textDecoration: "underline" }}
              key={i}
            >
              {formatted[formatted.length - 1]}
            </span>
          );
        } else {
          stack.push(c);
        }
        break;
      default:
        if (stack.length === 0) {
          if (typeof formatted[formatted.length - 1] === "string")
            formatted[formatted.length - 1] += c;
          else formatted.push(c);
        } else {
          s += c;
        }
    }
  }
  if (s.length > 0) formatted.push(s);

  return formatted;
}

// Browser compatible version of hashEmail
export async function hashEmail(email) {
  if (!email) return null;

  if (typeof window === "undefined") return null;

  const msgUint8 = new TextEncoder().encode(email.trim().toLowerCase()); // encode to UTF-8
  const hashBuffer = await window?.crypto?.subtle?.digest("SHA-256", msgUint8); // hash the message
  const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join(""); // bytes to hex
  return hashHex;
}

export function extractPlainText(rawJson) {
  // Helper function to traverse the content tree and extract text
  function extractTextFromContent(content) {
    let text = "";
    content.forEach((node) => {
      if (node.nodeType === "text") {
        text += node.value; // Add the text value
      } else if (node.content) {
        text += extractTextFromContent(node.content); // Recursively extract text from child nodes
      }
    });
    return text;
  }

  try {
    const parsedData = JSON.parse(rawJson);
    if (parsedData.nodeType === "document" && parsedData.content) {
      return extractTextFromContent(parsedData.content).trim();
    }

    return "";
  } catch (error) {
    console.error("Invalid raw JSON:", error);
    return "";
  }
}

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Parse a string with one or both of the following formats:
 *      - [text]{link}
 *      - [text]{hexColor}
 *
 * Returns elements:
 *      - <ALLink href={link}>text</ALLink>
 *      - <span style={{ color: hexColor }}>text</span>
 */
export function parseText(inputText) {
  const matches = Array.from(inputText.matchAll(LINK_COLOR_PATTERN));
  const parts = [];
  let lastIndex = 0;

  matches.forEach((match) => {
    if (match.index > lastIndex) {
      parts.push(inputText.substring(lastIndex, match.index));
    }

    const key = `match-${match.index}`;
    if (match[2].startsWith("#")) {
      parts.push(
        <span style={{ color: match[2] }} key={`color-${key}`}>
          {match[1]}
        </span>
      );
    } else {
      parts.push(
        <span
          key={`link-${key}`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <ALLink to={match[2]} rel="noopener noreferrer" className="underline">
            {match[1]}
          </ALLink>
        </span>
      );
    }

    lastIndex = match.index + match[0].length;
  });

  if (lastIndex < inputText.length) {
    parts.push(inputText.substring(lastIndex));
  }

  return parts;
}

/**
 * Exchanges a Shopify multipass token for a Storefront customer access token (browser).
 * @param {string} multipassToken
 * @param {{ name: string, storefrontAccessToken: string }} store
 */
export async function getCustomerAccessTokenCreateWithMultipass(multipassToken, store) {
  const tokenQuery = `
  mutation customerAccessTokenCreateWithMultipass {
    customerAccessTokenCreateWithMultipass(multipassToken: "${multipassToken}") {
      customerAccessToken {
        accessToken
        expiresAt
      }
      customerUserErrors {
        code
        field
        message
      }
    }
  }
  `;
  const resultToken = await fetch(`https://${store.name}.myshopify.com/api/2022-10/graphql.json`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/graphql",
      "X-Shopify-Storefront-Access-Token": store.storefrontAccessToken,
    },
    body: tokenQuery,
  });

  if (!resultToken.ok) {
    throw new Error(`${resultToken.status} ${resultToken.statusText}`);
  }

  const body = await resultToken.json();
  const { customerAccessToken, customerUserErrors } =
    body.data.customerAccessTokenCreateWithMultipass;

  if (customerUserErrors?.length) {
    const first = customerUserErrors[0];
    console.error(customerUserErrors);
    throw new Error(`${first?.code ?? ""} ${first?.message ?? ""}`.trim());
  }
  return customerAccessToken;
}
